import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import type { DetrixSession } from './types';
import { dummySession } from './dummyData';
import { SessionViewer } from './SessionViewer';
import { bootstrapSessionFromUrl } from './bootstrapSessionFromUrl';
import { applyThemeToDocument, resolveTheme } from './utils/theme';
import './index.css';

applyThemeToDocument(resolveTheme());

/** Must match `DETRIX_LAST_SESSION_STORAGE_KEY` in the extension content script. */
const DETRIX_LAST_SESSION_STORAGE_KEY = 'detrixLastSession';

const sessionApiBase = (import.meta.env.VITE_SESSION_API_BASE ?? '').trim().replace(/\/$/, '');

function shareIdFromPath(): string | null {
  const path = (window.location.pathname || '/').replace(/\/$/, '') || '/';
  const m = path.match(/^\/s\/([^/]+)$/);
  const raw = m?.[1];
  return raw ? decodeURIComponent(raw) : null;
}

async function loadSharedSession(shareId: string): Promise<DetrixSession> {
  if (!sessionApiBase) {
    throw new Error(
      'Share links need VITE_SESSION_API_BASE (Worker URL from apps/backend). With vite dev, use .env.development.local or .env.local — not only .env.production.local. Copy apps/studio/.env.example and restart Vite.',
    );
  }
  const res = await fetch(`${sessionApiBase}/sessions/${encodeURIComponent(shareId)}`);
  const body = (await res.json().catch(() => ({}))) as { error?: string; session?: DetrixSession };
  if (!res.ok) {
    throw new Error(body.error ?? `Failed to load session (${res.status})`);
  }
  if (!body.session || typeof body.session !== 'object' || !('sessionId' in body.session)) {
    throw new Error('Invalid session response from API');
  }
  return body.session;
}

async function loadFromExtensionStorageOrDummy(): Promise<DetrixSession> {
  try {
    if (typeof chrome !== 'undefined' && chrome.storage?.local?.get && chrome.runtime?.id) {
      const data = await chrome.storage.local.get(DETRIX_LAST_SESSION_STORAGE_KEY);
      const raw = data[DETRIX_LAST_SESSION_STORAGE_KEY];
      if (raw && typeof raw === 'object' && 'sessionId' in raw && 'events' in raw) {
        return raw as DetrixSession;
      }
    }
  } catch {
    // Not running as extension page, or storage blocked
  }
  return dummySession;
}

async function resolveInitialSession(): Promise<DetrixSession> {
  const shareId = shareIdFromPath();
  if (shareId) {
    return loadSharedSession(shareId);
  }
  return loadFromExtensionStorageOrDummy();
}

function renderError(rootEl: HTMLElement, message: string) {
  createRoot(rootEl).render(
    <StrictMode>
      <div className="flex min-h-screen flex-col gap-3 bg-[var(--bg-editor)] p-6 text-[var(--text-primary)]">
        <h1 className="text-lg font-semibold text-[var(--accent)]">Could not load session</h1>
        <pre className="mono-small overflow-auto whitespace-pre-wrap rounded border border-[var(--border)] bg-[var(--bg-panel)] p-3 text-[var(--text-secondary)]">
          {message}
        </pre>
      </div>
    </StrictMode>,
  );
}

const urlBootstrap = bootstrapSessionFromUrl();

void (async () => {
  const rootEl = document.getElementById('root');
  if (!rootEl) return;
  try {
    const session = await resolveInitialSession();
    createRoot(rootEl).render(
      <StrictMode>
        <SessionViewer session={session} {...urlBootstrap} />
      </StrictMode>,
    );
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    renderError(rootEl, message);
  }
})();
